import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import BrandLogosStrip from '@/pages/home/components/BrandLogosStrip';

function useVisible(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

const values = [
  {
    icon: 'ri-heart-line',
    title: 'Respect',
    description: 'We respect each other, our guests, our partners, and our communities.',
    color: 'text-rose-400',
    bg: 'bg-rose-500/10',
  },
  {
    icon: 'ri-lightbulb-line',
    title: 'Innovation',
    description: 'We drive results through creativity, technology, and bold thinking.',
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
  },
  {
    icon: 'ri-service-line',
    title: 'Service',
    description: 'Unsurpassed service for guests, employees, and business partners — always.',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
  },
  {
    icon: 'ri-award-line',
    title: 'Excellence',
    description: 'We exceed expectations by investing in our people, processes, and properties.',
    color: 'text-sky-400',
    bg: 'bg-sky-500/10',
  },
];

function HeroAbout() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative h-[520px] md:h-[640px] flex items-end overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Elegant%20hotel%20lobby%20interior%20with%20warm%20lighting%2C%20marble%20floors%2C%20modern%20hospitality%20design%2C%20sophisticated%20reception%20area%20with%20tall%20ceilings%2C%20soft%20amber%20and%20cream%20tones%2C%20luxury%20boutique%20hotel%20atmosphere%2C%20architectural%20photography%2C%20wide%20angle%2C%20no%20people%2C%20clean%20minimalist%20aesthetic%20with%20rich%20textures&width=1920&height=900&seq=about-hero-360&orientation=landscape"
          alt="360 Hospitality Group — About Us"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-slate-950/20"></div>
      </div>
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 pb-16 md:pb-20">
        <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center space-x-2 mb-5">
            <div className="h-px w-8 bg-amber-400"></div>
            <span className="text-xs font-medium text-amber-400 uppercase tracking-[0.25em]">About 360 Hospitality Group</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-light text-white leading-tight tracking-tight">
            Who We Are
          </h1>
          <p className="mt-4 text-lg text-gray-300 font-light max-w-xl leading-relaxed">
            Florida's performance-first hotel management partner — built by owners, for owners.
          </p>
        </div>
      </div>
    </section>
  );
}

function OriginStory() {
  const { ref, visible } = useVisible();
  const [hoveredPartner, setHoveredPartner] = useState<number | null>(null);

  const partners = [
    {
      name: 'Dilesh Patidar',
      title: 'Managing Partner',
      tagline: 'Founder & Hospitality Veteran',
      bio: 'With 25+ years across global hospitality markets, Dilesh founded 360 Hospitality Group on a simple belief, that every hotel owner deserves a management partner who treats their property like their own. His hands-on approach and deep operational expertise have shaped 360 into Florida\'s most trusted management firm.',
      image: 'https://storage.readdy-site.link/project_files/390fbdda-ccd9-47f3-ab63-db1f61ff7592/76619e9b-a14f-444f-a502-3306e372d0ad_Gemini_Generated_Image_j54rocj54rocj54r-neww.webp?v=dbf3f616ac8c60462936d70f46c11462',
      accent: 'from-amber-500/20 to-amber-500/5',
      border: 'border-amber-200',
      badge: 'bg-amber-500',
    },
    {
      name: 'Deep Patel',
      title: 'Managing Partner',
      tagline: 'Growth Strategist & Operator',
      bio: 'Deep brings a sharp commercial mindset and a passion for performance-driven hospitality. With expertise spanning revenue optimization, brand development, and owner relations, he partners closely with hotel owners to unlock the full potential of every asset,&nbsp; delivering measurable results from day one.',
      image: 'https://storage.readdy-site.link/project_files/390fbdda-ccd9-47f3-ab63-db1f61ff7592/1e002f0d-55e0-491d-91f7-dc2908240cf2_Pentus-Health-Deep-Patel-neww.webp?v=d6c3977cdc6e597aa22db604d4b8b266',
      accent: 'from-slate-200/60 to-slate-100/20',
      border: 'border-slate-200',
      badge: 'bg-slate-700',
    },
  ];

  return (
    <section ref={ref} className="py-28 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* Section Header */}
        <div className={`text-center mb-20 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center space-x-3 mb-6">
            <div className="h-px w-10 bg-amber-500"></div>
            <span className="text-xs font-medium text-amber-600 uppercase tracking-[0.3em]">Our Story</span>
            <div className="h-px w-10 bg-amber-500"></div>
          </div>
          <h2 className="text-5xl md:text-6xl font-light text-slate-900 leading-tight mb-6">
            The People Behind
            <br />
            <span className="text-amber-500 italic">360 Hospitality</span>
          </h2>
          <p className="text-base text-slate-500 font-light max-w-2xl mx-auto leading-relaxed">
            360 Hospitality Group was built on a simple belief — that every hotel owner deserves a management partner who treats their property like their own. Led by two seasoned operators, we bring institutional expertise with personal dedication.
          </p>
        </div>

        {/* Partners Grid */}
        <div className="grid lg:grid-cols-2 gap-8">
          {partners.map((partner, i) => (
            <div
              key={i}
              className={`group relative rounded-3xl overflow-hidden border transition-all duration-500 ${partner.border} ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{ transitionDelay: `${i * 150}ms` }}
              onMouseEnter={() => setHoveredPartner(i)}
              onMouseLeave={() => setHoveredPartner(null)}
            >
              {/* Background gradient */}
              <div className={`absolute inset-0 bg-gradient-to-br ${partner.accent} transition-opacity duration-500 ${hoveredPartner === i ? 'opacity-100' : 'opacity-60'}`}></div>

              <div className="relative z-10 p-8 md:p-10">
                {/* Top: Photo + Name */}
                <div className="flex items-start gap-6 mb-8">
                  {/* Photo */}
                  <div className="relative shrink-0">
                    <div className="w-28 h-28 md:w-32 md:h-32 rounded-2xl overflow-hidden ring-4 ring-white shadow-lg">
                      <img
                        src={partner.image}
                        alt={partner.name}
                        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    {/* Badge */}
                    <div className={`absolute -bottom-2 -right-2 w-8 h-8 flex items-center justify-center rounded-full ${partner.badge} shadow-md`}>
                      <i className="ri-star-fill text-white text-xs"></i>
                    </div>
                  </div>

                  {/* Name & Title */}
                  <div className="pt-1">
                    <div className="inline-flex items-center space-x-2 mb-2">
                      <span className="text-xs font-semibold text-amber-600 uppercase tracking-[0.2em] bg-amber-50 px-3 py-1 rounded-full border border-amber-100">
                        {partner.title}
                      </span>
                    </div>
                    <h3 className="text-2xl md:text-3xl font-semibold text-slate-900 leading-tight mb-1">
                      {partner.name}
                    </h3>
                    <p className="text-sm text-slate-500 font-light">{partner.tagline}</p>
                  </div>
                </div>

                {/* Divider */}
                <div className="h-px bg-slate-200 mb-8"></div>

                {/* Bio */}
                <p className="text-sm md:text-base text-slate-600 font-light leading-relaxed">
                  {partner.bio}
                </p>

                {/* Hover accent line */}
                <div className={`absolute bottom-0 left-0 h-1 bg-amber-500 transition-all duration-500 ${hoveredPartner === i ? 'w-full' : 'w-0'}`}></div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom quote */}
        <div className={`mt-16 text-center transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center space-x-4 px-8 py-5 bg-slate-50 rounded-2xl border border-slate-100">
            <i className="ri-double-quotes-l text-3xl text-amber-400"></i>
            <p className="text-sm md:text-base text-slate-600 font-light italic max-w-xl">
              Together, we bring the full 360° — operational depth, commercial strategy, and an unwavering commitment to every owner we serve.
            </p>
            <i className="ri-double-quotes-r text-3xl text-amber-400"></i>
          </div>
        </div>

      </div>
    </section>
  );
}

function MissionVision() {
  const { ref, visible } = useVisible();
  return (
    <section ref={ref} className="py-24 bg-slate-950">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center space-x-2 mb-6">
            <div className="h-px w-8 bg-amber-500"></div>
            <span className="text-xs font-medium text-amber-500 uppercase tracking-[0.25em]">Purpose &amp; Direction</span>
            <div className="h-px w-8 bg-amber-500"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-light text-white leading-tight">
            Mission &amp; Vision
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Mission */}
          <div className={`transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="h-full bg-amber-500 rounded-2xl p-10 flex flex-col">
              <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl mb-6">
                <i className="ri-focus-3-line text-2xl text-white"></i>
              </div>
              <p className="text-xs font-medium text-white/70 uppercase tracking-[0.25em] mb-4">Our Mission</p>
              <blockquote className="text-xl md:text-2xl font-light text-white leading-relaxed flex-1">
                "Our mission is to be the leading provider of exceptional hospitality experiences — exceeding expectations for every guest, associate, and owner we work with."
              </blockquote>
            </div>
          </div>

          {/* Vision */}
          <div className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="h-full bg-slate-800 rounded-2xl p-10 flex flex-col border border-slate-700">
              <div className="w-12 h-12 flex items-center justify-center bg-amber-500/20 rounded-xl mb-6">
                <i className="ri-eye-line text-2xl text-amber-400"></i>
              </div>
              <p className="text-xs font-medium text-amber-400/70 uppercase tracking-[0.25em] mb-4">Our Vision</p>
              <blockquote className="text-xl md:text-2xl font-light text-gray-200 leading-relaxed flex-1">
                "To transform every property we manage into a high-performing asset that delivers consistent revenue growth, outstanding guest satisfaction, and long-term value for our partners."
              </blockquote>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CoreValues() {
  const { ref, visible } = useVisible();
  return (
    <section ref={ref} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className={`mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center space-x-2 mb-6">
            <div className="h-px w-8 bg-amber-500"></div>
            <span className="text-xs font-medium text-amber-600 uppercase tracking-[0.25em]">What Drives Us</span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="text-4xl md:text-5xl font-light text-slate-900 leading-tight">
              Four Values.<br />
              <span className="text-amber-500 italic">Every Decision.</span>
            </h2>
            <p className="text-base text-slate-500 font-light max-w-sm leading-relaxed">
              These aren't wall art. They're the operating principles behind every property we manage and every partner we serve.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {values.map((v, i) => (
            <div
              key={i}
              className={`group p-8 rounded-2xl border border-slate-100 hover:border-slate-200 bg-slate-50 hover:bg-white transition-all duration-500 cursor-default ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="flex items-start space-x-5">
                <div className={`w-14 h-14 flex items-center justify-center rounded-xl shrink-0 ${v.bg} transition-transform duration-300 group-hover:scale-110`}>
                  <i className={`${v.icon} text-2xl ${v.color}`}></i>
                </div>
                <div>
                  <h3 className="text-xl font-medium text-slate-900 mb-2">{v.title}</h3>
                  <p className="text-sm text-slate-500 font-light leading-relaxed">{v.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AboutCTA() {
  const { ref, visible } = useVisible();
  return (
    <section ref={ref} className="py-24 bg-slate-950">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center space-x-2 mb-6">
            <div className="h-px w-8 bg-amber-500"></div>
            <span className="text-xs font-medium text-amber-500 uppercase tracking-[0.25em]">Ready to Partner?</span>
            <div className="h-px w-8 bg-amber-500"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-light text-white leading-tight mb-6">
            Let's Talk About<br />
            <span className="text-amber-400 italic">Your Property</span>
          </h2>
          <p className="text-base text-gray-400 font-light leading-relaxed mb-10 max-w-xl mx-auto">
            No pitch deck. No junior rep. A direct conversation with a 360 senior partner about what your hotel should be doing better — this week.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/"
              className="group px-6 py-3 rounded-full bg-[#1a2b3c] hover:bg-[#243447] border border-amber-400/40 hover:border-amber-400/70 text-white text-sm font-medium tracking-wide transition-all duration-300 whitespace-nowrap flex items-center space-x-2"
            >
              <span>Get a Free Assessment</span>
              <i className="ri-arrow-right-line text-base transition-transform group-hover:translate-x-1"></i>
            </Link>
            <Link
              to="/performance"
              className="group px-6 py-3 rounded-full border border-white/30 text-white text-sm font-light tracking-wide hover:border-amber-400/60 hover:bg-white/5 transition-all duration-300 whitespace-nowrap flex items-center space-x-2"
            >
              <span>Our Performance Model</span>
              <i className="ri-arrow-right-line text-base text-amber-400 transition-transform group-hover:translate-x-1"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <HeroAbout />
      <OriginStory />
      <BrandLogosStrip />
      <MissionVision />
      <CoreValues />
      <AboutCTA />
      <Footer />
    </div>
  );
}
